import { FC, useContext, useState } from 'react';
import { SerpResponse } from 'ts/hotel/interfaces/SerpResponse';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useTranslation } from 'react-i18next';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleArrowRight, faGlobe, faPhone, faThumbsDown, faThumbsUp } from '@fortawesome/free-solid-svg-icons';
import { faSquareArrowDownRight, faSquareArrowUpRight } from '@fortawesome/sharp-duotone-solid-svg-icons';
import { MainContext } from 'contexts/MainContext';
import { b2b } from 'constants/generic';

interface GoogleTabContentProps {
  res?: SerpResponse;
  activeTab: number;
}
const GoogleTabContent: FC<GoogleTabContentProps> = ({ res, activeTab }) => {
  const colorContrast = getComputedStyle(document.body).getPropertyValue('--color-contrast');
  const { user } = useContext(MainContext);
  const { t } = useTranslation();

  const featuredPrices = res?.serpResults.serpHotel.featuredPrices
    ?.filter((pr) => pr.rooms?.some((rc) => rc.name?.trim()))
    .sort((a) => (a.source === 'Booking.com' ? -1 : 0));
  const [visibleArr, setVisibleArr] = useState<number[]>(() =>
    featuredPrices?.map((_, index) => index) ?? [],
  );
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const checkInTime = res?.serpResults.serpHotel.checkInTime?.trim();
  const checkOutTime = res?.serpResults.serpHotel.checkOutTime?.trim();

  if (!res || !res.serpResults || !res.serpResults.serpHotel) return null;
  if (![0, 1, 2, 3].includes(activeTab)) return null;
  return (
    <div>
      <div className='mb-10 mt-5 display-flex flex-row flex-wrap flex-v-center text-sm'>
        {!!res.serpResults.serpHotel.link && (
          <Tooltip content={t('hotel.view_on_website')} position="top">
            <a
              href={res.serpResults.serpHotel.link}
              className="mr-10"
              target="_blank"
              rel="noreferrer"
            >
              <FontAwesomeIcon icon={faGlobe} color="#2563eb" />
            </a>
          </Tooltip>
        )}
        {!!checkInTime && (
          <div>
            <Tooltip content={t('search.check_in')} position="top">
              <FontAwesomeIcon icon={faSquareArrowUpRight as any} className="mr-5" />
            </Tooltip>
            <span className="text-bold">{checkInTime}</span>
          </div>
        )}
        {!!checkInTime && !!checkOutTime && <span className="ph-10">-</span>}
        {!!checkOutTime && (
          <span className="mr-10">
            <Tooltip content={t('search.check_out')} position="top">
              <FontAwesomeIcon icon={faSquareArrowDownRight as any} className="mr-5" />
            </Tooltip>
            <span className="text-bold">{checkOutTime}</span>
          </span>
        )}
        {user?.branchId === 5000 && (
          <span className="mr-10">
            <Tooltip content={t('passenger.phone')} position="top">
              <FontAwesomeIcon icon={faPhone} className="mr-5" />
            </Tooltip>
            <span className="text-bold">{res.serpResults.serpHotel.phone}</span>
          </span>
        )}
      </div>
      {activeTab === 0 && (
        <div className="text-lh">
          <div className="display-flex flex-wrap flex-v-center">
            {b2b && featuredPrices?.map((pr: any, index: number) => {
              const rooms = pr.rooms.filter((rc: any) => rc.name?.trim());
              const isExpanded = visibleArr.includes(index);
              const visibleRooms = isExpanded ? rooms : rooms.slice(0, 1);

              return (
                <div className="h-price" key={index}>
                  {visibleRooms?.map((rc: any, roomIndex: number) => {
                    const imageKey = `${index}-${roomIndex}`;
                    const imageSrc = rc.images?.[0];
                    const showImage = !!imageSrc && !failedImages.has(imageKey);
                    const showImageSlot = roomIndex === 0 || !!imageSrc;

                    const roomImage = showImageSlot ? (
                      <div
                        className="position-relative mr-10"
                        style={{ flexShrink: 0, width: 60, height: 60 }}
                      >
                        {showImage ? (
                          <img
                            src={imageSrc}
                            alt={rc.name}
                            style={{
                              width: 60,
                              height: 60,
                              objectFit: 'cover',
                              borderRadius: 4,
                              display: 'block',
                            }}
                            onError={() =>
                              setFailedImages((prev) => new Set(prev).add(imageKey))
                            }
                          />
                        ) : null}
                        {!!pr.logo && (
                          <img
                            src={pr.logo}
                            alt={pr.source}
                            style={{
                              position: 'absolute',
                              right: 2,
                              width: 'auto',
                              bottom: 2,
                              height: 20,
                              background: '#fff',
                              borderRadius: 2,
                            }}
                          />
                        )}
                      </div>
                    ) : null;

                    const priceBlock = (
                      <div className="display-flex">
                        <Tooltip
                          position="top"
                          content={rc.ratePerNight.lowest + ' / ' + t('hotel.price_per_night')}
                        >
                          <div className="ph-10">{rc.totalRate.lowest}</div>
                        </Tooltip>
                        <a href={rc.link} target="_blank" rel="noreferrer">
                          <FontAwesomeIcon
                            icon={faCircleArrowRight}
                            color={colorContrast}
                            size="xl"
                          />
                        </a>
                      </div>
                    );

                    if (roomIndex === 0) {
                      return (
                        <div key={roomIndex} className="flex-row flex-v-start">
                          {roomImage}
                          <div className="flex-one">
                            <div className="flex-row flex-between" style={{ flexWrap: 'nowrap' }}>
                              <div className="flex-column">
                                <div className="text-bold">{rc.name}</div>
                                <span className="text-light text-xsm">
                                  {rc.numGuests} {t('passenger.guests')}
                                </span>
                              </div>
                              {priceBlock}
                            </div>

                            {rooms.length > 1 && (
                              <div
                                className="text-xsm text-contrast cur-poi"
                                onClick={() =>
                                  setVisibleArr(
                                    isExpanded
                                      ? visibleArr.filter((i) => i !== index)
                                      : [...visibleArr, index]
                                  )
                                }
                              >
                                {isExpanded ? t('show_less') : t('show_more')}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={roomIndex} className="flex-between pt-10 pb-5 bt-1">
                        <div className="display-flex flex-v-start mb-5">
                          {roomImage}
                          <div>
                            <div className="text-bold">{rc.name}</div>
                            <div className="text-light text-xsm">
                              {rc.numGuests} {t('passenger.guests')}
                            </div>
                          </div>
                        </div>
                        {priceBlock}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            <div></div>
          </div>
        </div>
      )}
      {activeTab === 1 && (
        <div className="text-lh">
          {res.serpResults.serpHotel.description && (
            <div>
              <div className=' text-bold text-lg mt-20'>{t('hotel.description')}</div>
              <div className='text-lh text-sm mb-20 mt-10'>{res.serpResults.serpHotel.description}</div>
            </div>
          )}
        </div>
      )}
      {activeTab === 2 && (
        <div>
          <div className="h-ratings-wr">
            <div className="h-ratings">
              <div className="h-ratings-number">{res.serpResults.serpHotel.overallRating}</div>
              <div className="text-light">
                {res.serpResults.serpHotel.reviews} {t('hotel.reviews')}
              </div>
            </div>
            <div className="h-ratings-list">
              {res.serpResults.serpHotel.ratings?.map((rat, index) => (
                <div key={index} className="h-ratings-row-wr">
                  <div className="h-ratings-star text-sm">
                    {rat.stars} {t('sort.star')}
                  </div>

                  <div className="h-ratings-row">
                    <div
                      className="h-ratings-row-yellow"
                      style={{
                        width: (rat.count / res.serpResults.serpHotel.reviews) * 100 + '%',
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-10 h-ratings-breakdown">
            {res.serpResults.serpHotel.reviewsBreakdown
              ?.sort(
                (a, b) =>
                  b.positive / b.totalMentioned -
                  b.negative / b.totalMentioned -
                  (a.positive / a.totalMentioned - a.negative / a.totalMentioned)
              )
              .map((rew, index) => (
                <div className="h-ratings-breakdown-item" key={index}>
                  <Tooltip
                    position="top"
                    content={[
                      { val: rew.positive, text: t('hotel.positive') },
                      { val: rew.neutral, text: t('hotel.neutral') },
                      { val: rew.negative, text: t('hotel.negative') },
                    ]
                      .filter((f) => f.val)
                      .map((f) => f.text + ': ' + f.val)
                      .join(', ')}
                  >
                    <span>
                      {rew.positive / rew.totalMentioned - rew.negative / rew.totalMentioned >
                        0.42 && (
                          <FontAwesomeIcon icon={faThumbsUp} color="green" className="mr-5" />
                        )}
                      {rew.positive / rew.totalMentioned - rew.negative / rew.totalMentioned <
                        -0.42 && (
                          <FontAwesomeIcon icon={faThumbsDown} color="red" className="mr-5" />
                        )}
                      {rew.name} <span className="text-light">({rew.totalMentioned}) </span>
                    </span>
                  </Tooltip>
                </div>
              ))}
          </div>
        </div>
      )}

      {activeTab === 3 && (
        <div className="pr-photos-wr">
          {res.serpResults.serpHotel.images?.map((im, index) => (
            <div key={index} className="h-photos">
              <img
                src={im.originalImage}
                alt="1"
                onError={(e) => ((e.target as any).parentNode.style.display = 'none')}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary(GoogleTabContent, 'GoogleTabContent');
